import React, { useContext, useRef } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import classNames from 'classnames';

import { ContentType, LessonMode } from '../../../Constants';

import navMenuService from '../../services/NavMenuService';

import useAccessibilityClick from '../../../hooks/useAccessibilityClick';

import responseService from '../../services/ResponseService';
import questionService from '../../services/QuestionService';
import UtilsService from '../../services/UtilsService';
import ResourceIcon from '../ResourceIcon';

import { register } from '../../../i18n';

const t = register('SummaryInfo');

const isActivity = (model, lessonElementId, lessonManager) => {
  const lessonElement = lessonManager.getLessonElement(lessonElementId);
  if (model.isActivity && lessonElement.lessonElementIds && lessonElement.lessonElementIds.length > 0) {
    return true;
  }
  return false;
};

const SummaryCard = observer(({ lessonElementId }) => {
  const {
    lessonManager,
    questionFeedbackManager,
    responseManager,
    videoManager,
    audioManager
  } = useContext(MobXProviderContext);

  const mode = lessonManager.playerMode;
  const model = lessonManager.getSlideModel(lessonElementId);
  const state = responseManager.getLessonElementState(lessonElementId);
  const cardRef = useRef();

  // TODO unused // const status = (studentActivityManager && studentActivityManager.activityInstanceStatus) ? studentActivityManager.activityInstanceStatus : ActivityStatus.ACTIVITY_STARTED;

  const notReady = (!model);
  const isAnsweredSurvey = questionService.isAnsweredSurveyQuestion({
    lessonElementId: model.lessonElementId,
    lessonElementState: state,
    model
  });

  const isSubmitted = !!state?.isSubmitted || isAnsweredSurvey || model.offlineGrading;
  const hasFeedback = questionFeedbackManager.hasFeedback || (mode === LessonMode.REVIEW) || (mode === LessonMode.SCORING);
  const correct = responseManager.correctList.includes(lessonElementId) && hasFeedback;
  const notScored = responseManager.notScoredList.includes(lessonElementId) && hasFeedback;
  const unScored = responseManager.unscoredList.includes(lessonElementId) && hasFeedback;
  const incorrect = responseManager.incorrectList.includes(lessonElementId) && hasFeedback;
  const partially = responseManager.partiallyCorrectList.includes(lessonElementId) && hasFeedback;
  const flagged = responseManager.markedForReviewList.includes(lessonElementId);
  const showTeacherFeedbackIcon = (lessonManager.playerMode === LessonMode.REVIEW &&
    state && state.comment &&
    !UtilsService.isNullOrEmpty(state.comment)
  );

  const isTestItem = (model) ? (model.isTestItem && model.type !== ContentType.LIKERT.type) || isActivity(model, lessonElementId, lessonManager) : false;
  const isResource = (model) ? (model.isResource || model.type === ContentType.WEB_PAGE.type || model.type === ContentType.LIKERT.type) : false;

  const type = (model) ? model.type : null;
  // eslint-disable-next-line eqeqeq
  const isInstruction = (model) ? (model.isInstruction && model.type !== ContentType.WEB_PAGE.type) : false;

  let isLocked = (state && state.isDependencyLocked);
  let parentLocked = false;
  if (model && model.isActivityPart) {
    const parentState = responseManager.getLessonElementState(model.parentActivityElementId);
    if (parentState && parentState.isDependencyLocked) {
      isLocked = true;
      parentLocked = true;
    }
  }

  useAccessibilityClick(cardRef, async (_event) => {
    if (isLocked) {
      if (parentLocked) {
        const parentState = responseManager.getLessonElementState(model.parentActivityElementId);
        await responseService.alertDependencyLock(parentState);
      } else {
        await responseService.alertDependencyLock(state);
      }
      return;
    }
    const parentModel = lessonManager.getSlideModel(model?.parentActivityElementId);

    if (parentModel?.type === ContentType.VIDEO_QUESTION.type) {
      videoManager.setSideNavVideoProgressValue(model.showTime);
      videoManager.setSelectedSideNavVideoChildLessonElementId(lessonElementId);
      const slides = lessonManager.getFilteredSlides();
      const index = slides.indexOf(lessonElementId);
      lessonManager.setCurrentSlideIndex(index);
      videoManager.setShouldCallVideoScrubChangeHandler(true);
      await navMenuService.navToSlideClickHandler(lessonElementId, { fromCardHandler: true });
    } else if (parentModel?.type === ContentType.AUDIO_QUESTION.type) {
      audioManager.setSideNavAudioProgressValue(model.showTime);
      audioManager.setSelectedSideNavAudioChildLessonElementId(lessonElementId);
      const slides = lessonManager.getFilteredSlides();
      const index = slides.indexOf(lessonElementId);
      lessonManager.setCurrentSlideIndex(index);
      audioManager.setShouldCallAudioScrubChangeHandler(true);
      await navMenuService.navToSlideClickHandler(lessonElementId, { fromCardHandler: true });
    } else if (parentModel?.type === ContentType.PDF_ACTIVITY.type) {
      await navMenuService.navToSlideClickHandler(lessonElementId, {
        fromCardHandler: true,
        shouldReturnIfSameLessonElementId: false,
      });
    } else {
      await navMenuService.navToSlideClickHandler(lessonElementId, { fromCardHandler: true });
    }
  });

  const renderTestItemCard = () => {
    return (
      <li ref={cardRef} className={classNames({
        'summary-card': true,
        'un-scored': (unScored),
        'not-scored': (notScored),
        'answered': (isSubmitted),
        'is-correct': (correct),
        'is-incorrect': (incorrect),
        'is-partial': (partially),
        'is-flagged': (flagged),
        'is-dependent-locked': (isLocked)
  
      })} role='button'>
        <div className='background-color' />
        <div className='card-info'>
          {(showTeacherFeedbackIcon) && <ResourceIcon altText='Teacher Feedback' className='TeacherFeedback' maskOff={true} />}
          <div className='card-top-row'>
            <div className='summary-card-label'>
              {model.questionNumber}
            </div>
            <div className={classNames({
              'icons': true,
              'visible': ((flagged || !isSubmitted) && !model.isInstruction),
              'both-icons': flagged && !isSubmitted
            })}>
              {(flagged) && <div aria-label={t('markedForReview')} className='flagged icon' role='img' />}
              {(!isSubmitted) && <div aria-label={t('unanswered')} className='not-answered icon' role='img' />}
            </div>
          </div>
          {((hasFeedback && isSubmitted) || mode === LessonMode.REVIEW) && (
            <div className='card-bottom-row'>
              {(correct) && <div aria-label={t('correct')} className='correct icon' role='img' />}
              {(incorrect) && <div aria-label={t('incorrect')} className='incorrect icon' role='img' />}
              {(partially) && <div aria-label={t('partiallyCorrect')} className='partial icon' role='img' />}
              {(notScored) && <div aria-label={t('toBeScored')} className='not-scored icon' role='img' />}
              {(unScored) && <div aria-label={t('notToBeScored')} className='un-scored icon' role='img' />}
            </div>
          )}
        </div>

      </li>
    );
  };

  const renderInstructionCard = () => {
    return (
      <li ref={cardRef} className={classNames({
        'summary-card': true,
        'is-instruction': true,
        'has-feedback': (hasFeedback),
        'is-dependent-locked': (isLocked)
      })} role='button'>
        <div className='background-color' />
        <div className='card-info'>
          {(showTeacherFeedbackIcon) && <ResourceIcon altText='Teacher Feedback' className='TeacherFeedback' maskOff={true} />}
          <div className='card-top-row'>
            <div className='instruction-wrapper'>
              <div aria-label={t('instructions')} className='instruction-icon' role='img' />
            </div>
            <div className={classNames({ icons: true, visible: flagged })}>
              {(flagged) && <div aria-label={t('markedForReview')} className='flagged icon' role='img' />}
            </div>
          </div>
        </div>
      </li>
    );
  };

  const renderResourceCard = () => {
    return (
      <li ref={cardRef} className={classNames({
        'summary-card': true,
        'answered': (isSubmitted && model.type === ContentType.LIKERT.type),
        'has-feedback': hasFeedback,
        'is-resource': true,
        'is-dependent-locked': (isLocked)
      })} role='button'>
        <div className='background-color' />
        <div className='card-info'>
          {(showTeacherFeedbackIcon) && <ResourceIcon altText='Teacher Feedback' className='TeacherFeedback' maskOff={true} />}
          <div className='card-top-row'>
            <ResourceIcon altText={ContentType.getLabel(type)} className={type} />
            <div className={classNames({ icons: true, visible: flagged })}>
              {(flagged) && <div aria-label={t('markedForReview')} className='flagged icon' role='img' />}
            </div>
          </div>
        </div>

      </li>
    );
  };

  const hasInstructionNumber = isInstruction && !UtilsService.isNullOrEmpty(model.questionNumber);

  return (
    <>
      {notReady && <></>}
      {(isTestItem || hasInstructionNumber) && renderTestItemCard()}
      {isInstruction && !hasInstructionNumber && renderInstructionCard()}
      {isResource && renderResourceCard()}
    </>
  );
});

export default SummaryCard;
